import { useCallback, useEffect, useState } from "react";
import { format, addDays, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Printer, PhoneCall, CalendarSync, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, SHIFT_LABELS, SHIFT_STYLES, EVENT_LABELS, EVENT_BADGE_STYLES, OT_STYLE, initials, onCallLabel, photoUrl } from "@/lib/api";
import MonthView from "@/components/MonthView";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const SHIFT_OPTIONS = [
  { value: "working", label: "Working 8am – 5pm" },
  { value: "on_call", label: "On Call" },
  { value: "off", label: "Off" },
];

export default function Schedule() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [data, setData] = useState(null);
  const [cell, setCell] = useState(null); // {employee, date, shift}
  const [dayIdx, setDayIdx] = useState(0);
  const [ocTimes, setOcTimes] = useState({ start: "17:00", end: "08:00" });
  const [otWeek, setOtWeek] = useState([]);
  const [otBi, setOtBi] = useState([]);
  const [showSync, setShowSync] = useState(false);
  const [view, setView] = useState("grid");

  const icsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/calendar.ics`;
  const copyIcs = () => {
    navigator.clipboard.writeText(icsUrl).then(() => toast.success("Calendar link copied — paste it in Outlook → Add calendar → Subscribe from web"));
  };

  const load = useCallback(() => {
    const ws = format(weekStart, "yyyy-MM-dd");
    api.get(`/schedule?week_start=${ws}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load schedule"));
    api.get(`/overtime/summary?start=${ws}&end=${format(addDays(weekStart, 6), "yyyy-MM-dd")}`)
      .then((r) => setOtWeek(r.data.totals)).catch(() => {});
    api.get(`/overtime/summary?start=${ws}&end=${format(addDays(weekStart, 13), "yyyy-MM-dd")}`)
      .then((r) => setOtBi(r.data.totals)).catch(() => {});
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const saveShift = async (shift) => {
    try {
      await api.put("/schedule", {
        employee_id: cell.employee.id, date: cell.date, shift,
        on_call_start: shift === "on_call" ? ocTimes.start : "",
        on_call_end: shift === "on_call" ? ocTimes.end : "",
      });
      toast.success(`${cell.employee.name} — ${SHIFT_OPTIONS.find((s) => s.value === shift)?.label}`);
      setCell(null);
      load();
    } catch {
      toast.error("Failed to save shift");
    }
  };

  if (!data) return <div className="py-20 text-center text-sm text-slate-400" data-testid="schedule-loading">Loading…</div>;

  const dates = data.dates.map((d) => new Date(d + "T12:00:00"));
  const today = format(new Date(), "yyyy-MM-dd");

  const shiftFor = (empId, dateStr, idx) => {
    const entry = data.entries?.[empId]?.[dateStr];
    if (entry) return entry.shift;
    return idx < 5 ? "working" : "off"; // default: Mon–Fri working, weekend off
  };

  const weekLabel = `${format(dates[0], "MMM d")} – ${format(dates[6], "MMM d, yyyy")}`;

  return (
    <div className="space-y-5" data-testid="schedule-page">
      <div className="no-print flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Staff Schedule</h1>
          {view !== "month" && <p className="mt-1 text-sm text-slate-500" data-testid="week-label">{weekLabel}</p>}
        </div>
        {view !== "month" && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="rounded-md" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="prev-week-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" className="rounded-md" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="today-btn">This Week</Button>
            <Button variant="outline" size="icon" className="rounded-md" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="next-week-btn"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
        <Button className="rounded-md bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={() => window.print()} data-testid="print-schedule-btn">
          <Printer className="mr-2 h-4 w-4" /> Print / Export
        </Button>
        <Button variant="outline" className="rounded-full border-[#2C5545]/30 text-[#2C5545] hover:bg-[#2C5545]/10" onClick={() => setShowSync(true)} data-testid="sync-calendar-btn">
          <CalendarSync className="mr-2 h-4 w-4" /> Sync to Outlook
        </Button>
      </div>

      {view !== "month" && otWeek.length > 0 && (
        <div className="no-print flex flex-wrap gap-2" data-testid="ot-summary">
          {otWeek.map((t) => (
            <div key={t.employee_id} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${OT_STYLE}`} data-testid={`ot-week-${t.employee_id}`}>
              {t.name}: {t.total_hours}h OT this week · {otBi.find((b) => b.employee_id === t.employee_id)?.total_hours ?? t.total_hours}h biweekly
            </div>
          ))}
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="no-print">
        <TabsList className="rounded-md" data-testid="view-tabs">
          <TabsTrigger value="grid" data-testid="tab-grid">Weekly Grid</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">Day-by-Day</TabsTrigger>
          <TabsTrigger value="month" data-testid="tab-month">Month</TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="mt-4">
          <div className="overflow-x-auto rounded-2xl border border-white/40 bg-white/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-2xl" data-testid="schedule-grid">
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200/70 bg-white/50">
                  <th className="w-48 p-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">Staff</th>
                  {dates.map((d, i) => (
                    <th key={i} className={`p-3 text-center ${data.dates[i] === today ? "bg-slate-100" : ""}`}>
                      <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate-500">{format(d, "EEE")}</p>
                      <p className={`font-heading text-base font-semibold ${data.dates[i] === today ? "text-slate-900" : "text-slate-700"}`}>{format(d, "d")}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.employees.map((emp) => (
                  <tr key={emp.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`row-${emp.id}`}>
                    <td className="p-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={photoUrl(emp)} alt={emp.name} /><AvatarFallback className="bg-slate-100 text-xs font-semibold text-slate-700">{initials(emp.name)}</AvatarFallback></Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900">{emp.name}</p>
                          <p className="truncate text-xs text-slate-500">{emp.position}</p>
                        </div>
                      </div>
                    </td>
                    {data.dates.map((dateStr, i) => {
                      const shift = shiftFor(emp.id, dateStr, i);
                      const entry = data.entries?.[emp.id]?.[dateStr];
                      const events = data.timeoff?.[emp.id]?.[dateStr] || [];
                      const visits = data.visits?.[emp.id]?.[dateStr] || [];
                      const ot = data.overtime?.[emp.id]?.[dateStr];
                      return (
                        <td key={dateStr} className={`p-1.5 ${dateStr === today ? "bg-[#2C5545]/5" : ""}`}>
                          <button
                            className={`flex min-h-[64px] w-full flex-col items-center justify-center gap-1 rounded-xl border px-1 py-1.5 text-xs font-medium transition-transform duration-100 hover:scale-[1.03] hover:shadow-sm ${SHIFT_STYLES[shift]}`}
                            onClick={() => {
                              setCell({ employee: emp, date: dateStr, shift });
                              setOcTimes({ start: entry?.on_call_start || "17:00", end: entry?.on_call_end || "08:00" });
                            }}
                            data-testid={`cell-${emp.id}-${dateStr}`}
                          >
                            <span>{shift === "on_call" ? onCallLabel(entry) : SHIFT_LABELS[shift]}</span>
                            {ot > 0 && (
                              <span className={`rounded border px-1 py-px text-[10px] font-semibold ${OT_STYLE}`}>{ot}h OT</span>
                            )}
                            {events.map((ev) => (
                              <span key={ev.id} className={`rounded border px-1 py-px text-[10px] font-semibold ${EVENT_BADGE_STYLES[ev.type]}`}>{EVENT_LABELS[ev.type]}</span>
                            ))}
                            {visits.length > 0 && (
                              <span className="text-[10px] font-normal text-slate-500">{visits.length} visit{visits.length > 1 ? "s" : ""}</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <div className="mb-4 flex flex-wrap gap-1.5" data-testid="day-picker">
            {dates.map((d, i) => (
              <Button key={i} variant={i === dayIdx ? "default" : "outline"} size="sm"
                className={`rounded-md ${i === dayIdx ? "bg-[#2C5545] text-white hover:bg-[#1E3B30]" : ""}`}
                onClick={() => setDayIdx(i)} data-testid={`day-btn-${i}`}>
                {format(d, "EEE d")}
              </Button>
            ))}
          </div>
          <div className="space-y-2" data-testid="day-list">
            {data.employees.map((emp) => {
              const dateStr = data.dates[dayIdx];
              const shift = shiftFor(emp.id, dateStr, dayIdx);
              const entry = data.entries?.[emp.id]?.[dateStr];
              const events = data.timeoff?.[emp.id]?.[dateStr] || [];
              const visits = data.visits?.[emp.id]?.[dateStr] || [];
              return (
                <div key={emp.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid={`day-row-${emp.id}`}>
                  <Avatar className="h-9 w-9"><AvatarImage src={photoUrl(emp)} alt={emp.name} /><AvatarFallback className="bg-slate-100 text-xs font-semibold text-slate-700">{initials(emp.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{emp.name}</p>
                    <p className="text-xs text-slate-500">{emp.position}</p>
                  </div>
                  {visits.map((v) => (
                    <Badge key={v.id} variant="outline" className="hidden border-slate-200 text-slate-600 sm:inline-flex">{v.time || ""} {v.patient_name}</Badge>
                  ))}
                  {events.map((ev) => (
                    <Badge key={ev.id} variant="outline" className={EVENT_BADGE_STYLES[ev.type]}>{EVENT_LABELS[ev.type]}</Badge>
                  ))}
                  <button
                    className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:shadow-sm ${SHIFT_STYLES[shift]}`}
                    onClick={() => setCell({ employee: emp, date: dateStr, shift })}
                    data-testid={`day-shift-${emp.id}`}
                  >
                    {shift === "on_call" && <PhoneCall className="h-3 w-3" />}
                    {shift === "on_call" ? onCallLabel(entry) : shift === "working" ? "8am – 5pm" : SHIFT_LABELS[shift]}
                  </button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="month" className="mt-4">
          <MonthView />
        </TabsContent>
      </Tabs>

      {/* Print-friendly version */}
      <div className="print-only" id="print-area" data-testid="print-area">
        <h1 style={{ fontFamily: "Playfair Display", fontSize: 20, fontWeight: 600 }}>Hospice Care — Weekly Staff Schedule</h1>
        <p style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>{weekLabel}</p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>
              <th style={thStyle}>Staff</th>
              {dates.map((d, i) => <th key={i} style={thStyle}>{format(d, "EEE M/d")}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.employees.map((emp) => (
              <tr key={emp.id}>
                <td style={tdStyle}><strong>{emp.name}</strong><br /><span style={{ color: "#666" }}>{emp.position}</span></td>
                {data.dates.map((dateStr, i) => {
                  const shift = shiftFor(emp.id, dateStr, i);
                  const events = data.timeoff?.[emp.id]?.[dateStr] || [];
                  const visits = data.visits?.[emp.id]?.[dateStr] || [];
                  return (
                    <td key={dateStr} style={{ ...tdStyle, textAlign: "center" }}>
                      {shift === "working" ? "8am–5pm" : shift === "on_call" ? "On Call" : "Off"}
                      {events.map((ev) => <div key={ev.id} style={{ fontWeight: 600 }}>{EVENT_LABELS[ev.type]}</div>)}
                      {visits.map((v) => <div key={v.id} style={{ color: "#444" }}>{v.time} {v.patient_name}</div>)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!cell} onOpenChange={(o) => !o && setCell(null)}>
        <DialogContent className="rounded-lg sm:max-w-sm" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {cell?.employee.name} — {cell && format(new Date(cell.date + "T12:00:00"), "EEEE, MMM d")}
            </DialogTitle>
            <DialogDescription>Choose the shift for this day.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            {SHIFT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors hover:shadow-sm ${SHIFT_STYLES[opt.value]} ${cell?.shift === opt.value ? "ring-2 ring-[#2C5545]" : ""}`}
                onClick={() => opt.value === "on_call" ? setCell({ ...cell, shift: "on_call" }) : saveShift(opt.value)}
                data-testid={`shift-option-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
            {cell?.shift === "on_call" && (
              <div className="space-y-3 rounded-xl border border-[#D8B4FE] bg-[#F3E8FF]/60 p-3" data-testid="oncall-hours">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5B21B6]">On-call hours</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-[#5B21B6]">From</Label>
                    <Input type="time" value={ocTimes.start} onChange={(e) => setOcTimes({ ...ocTimes, start: e.target.value })} className="mt-1 bg-white" data-testid="oncall-start-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#5B21B6]">To</Label>
                    <Input type="time" value={ocTimes.end} onChange={(e) => setOcTimes({ ...ocTimes, end: e.target.value })} className="mt-1 bg-white" data-testid="oncall-end-input" />
                  </div>
                </div>
                <Button className="w-full rounded-full bg-[#5B21B6] text-white hover:bg-[#4C1D95]" onClick={() => saveShift("on_call")} data-testid="oncall-save-btn">
                  Save On-Call Shift
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSync} onOpenChange={setShowSync}>
        <DialogContent className="rounded-lg sm:max-w-lg" data-testid="sync-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Sync with Outlook & Other Calendars</DialogTitle>
            <DialogDescription>
              Copy the link below, then in Outlook go to <strong>Add calendar → Subscribe from web</strong> and paste it. Shifts and patient visits will appear and stay updated automatically. Works with Google Calendar ("From URL") too.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 pt-2">
            <Input readOnly value={icsUrl} onFocus={(e) => e.target.select()} className="text-xs" data-testid="ics-url-input" />
            <Button className="rounded-full bg-[#2C5545] text-white hover:bg-[#1E3B30]" onClick={copyIcs} data-testid="copy-ics-btn">
              <Copy className="mr-1 h-4 w-4" /> Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const thStyle = { border: "1px solid #cbd5e1", padding: "6px 8px", textAlign: "left", background: "#f1f5f9" };
const tdStyle = { border: "1px solid #cbd5e1", padding: "6px 8px", verticalAlign: "top" };
